import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  History,
  Search,
  Loader2,
  Trash2,
  Save,
  Calendar,
  User,
  Info,
  FileText,
} from "lucide-react";
import { EmptyState } from "@/components/consultation/EmptyState";
import {
  useInvestigationSearch,
  usePatientInvestigationNotes,
  useCreateInvestigationNote,
  useDeleteInvestigationNote,
} from "@/hooks/useInvestigationNotes";
import { ExpandedCardDialog } from "@/components/consultation/dialogs/ExpandedCardDialog";
import { useDebounce } from "@/hooks/use-debounce";
import type { InvestigationItem } from "@/types/appointments";
import { format } from "date-fns";
import { Command as CommandPrimitive } from "cmdk";

interface InvestigationNotesCardProps {
  appointmentId: string;
  isCompleted?: boolean;
}

interface DraftNote extends InvestigationItem {
  value: string;
}

const getInvestigationUnitDisplay = (item: InvestigationItem) =>
  item.type === "imaging" || !item.unit ? "" : item.unit;

const InvestigationSearch = ({
  appointmentId,
  onSelect,
  excludeIds = [],
  isCompleted = false,
}: {
  appointmentId: string;
  onSelect: (item: InvestigationItem) => void;
  excludeIds?: number[];
  isCompleted?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedSearch = useDebounce(query, 300);

  const { data: searchResults = [], isLoading: isSearching } =
    useInvestigationSearch({
      search: debouncedSearch,
      appointment_id: appointmentId,
    });

  const filteredResults = useMemo(() => {
    if (excludeIds.length === 0) return searchResults;
    return searchResults.filter((item) => !excludeIds.includes(item.id));
  }, [searchResults, excludeIds]);

  const handleSelect = (item: InvestigationItem) => {
    onSelect(item);
    setOpen(false);
    setQuery("");
  };

  return (
    <Command shouldFilter={false} className="overflow-visible bg-transparent">
      <div className="flex items-center px-3 h-10 border rounded-xl border-border/70 bg-background/80 focus-within:border-primary/60 transition-colors">
        <Search className="mr-2 size-4 shrink-0 text-muted-foreground" />
        <CommandPrimitive.Input
          placeholder="Search to add investigation..."
          value={query}
          onValueChange={(val) => {
            setQuery(val);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          className="flex h-full w-full bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 text-foreground z-body"
          disabled={isCompleted}
        />
      </div>

      {open && (query || filteredResults.length > 0) && (
        <div className="relative">
          <div className="absolute top-1 left-0 w-full z-50 rounded-xl border border-border/60 bg-card shadow-lg overflow-hidden animate-in fade-in-0 zoom-in-95">
            <CommandList className="max-h-[200px] overflow-y-auto scrollbar-thin">
              {isSearching && (
                <div className="py-4 text-center">
                  <Loader2 className="size-4 animate-spin mx-auto mb-1.5 text-muted-foreground" />
                  <span className="z-caption text-muted-foreground">
                    Searching...
                  </span>
                </div>
              )}
              {!isSearching && filteredResults.length === 0 && query && (
                <CommandEmpty className="py-4 px-2">
                  <span className="z-caption text-muted-foreground">
                    No results found.
                  </span>
                </CommandEmpty>
              )}
              {!isSearching &&
                filteredResults.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.name}
                    onSelect={() => handleSelect(item)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(item);
                    }}
                    className="px-3 py-2 cursor-pointer hover:bg-muted/50"
                  >
                    <div className="flex flex-col">
                      <span className="z-body-md text-foreground">
                        {item.name}
                      </span>
                      {getInvestigationUnitDisplay(item) && (
                        <span className="z-hint text-muted-foreground">
                          Unit: {getInvestigationUnitDisplay(item)}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
            </CommandList>
          </div>
        </div>
      )}
    </Command>
  );
};

export const InvestigationNotesCard = ({
  appointmentId,
  isCompleted = false,
}: InvestigationNotesCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [draftNotes, setDraftNotes] = useState<DraftNote[]>([]);

  const { data: notes = [], isLoading: isLoadingNotes } =
    usePatientInvestigationNotes(appointmentId, isExpanded);
  const createNote = useCreateInvestigationNote();
  const deleteNote = useDeleteInvestigationNote();

  const flattenedNotes = useMemo(() => {
    if (isExpanded) return [];
    return notes
      .flatMap((note) =>
        note.investigation_items.map((item) => ({
          ...item,
          doctor_name: note.doctor_name,
          date: note.created_at,
        }))
      )
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [notes, isExpanded]);

  const excludeIds = useMemo(() => {
    const savedIds = flattenedNotes.map((note) => note.investigation_item);
    const draftIds = draftNotes.map((draft) => draft.id);
    return [...new Set([...savedIds, ...draftIds])];
  }, [flattenedNotes, draftNotes]);

  const handleSelectInvestigation = (item: InvestigationItem) => {
    if (draftNotes.some((draft) => draft.id === item.id)) return;
    setDraftNotes((prev) => [{ ...item, value: "" }, ...prev]);
  };

  const handleUpdateDraftValue = (id: number, value: string) => {
    setDraftNotes((prev) =>
      prev.map((note) => (note.id === id ? { ...note, value } : note))
    );
  };

  const handleSaveDraft = (draft: DraftNote) => {
    if (!draft.value) return;
    createNote.mutate(
      {
        appointmentId,
        data: { investigation_item: draft.id, value: parseFloat(draft.value) },
      },
      {
        onSuccess: () =>
          setDraftNotes((prev) => prev.filter((n) => n.id !== draft.id)),
      }
    );
  };

  const handleDeleteDraft = (id: number) =>
    setDraftNotes((prev) => prev.filter((n) => n.id !== id));
  const handleDeleteNote = (noteId: string) =>
    deleteNote.mutate({ appointmentId, noteId });

  const CollapsedContent = () => (
    <div className="flex flex-col h-full">
      {!isCompleted && (
        <div className="px-4 py-3 border-b border-border/60">
          <InvestigationSearch
            appointmentId={appointmentId}
            onSelect={handleSelectInvestigation}
            excludeIds={excludeIds}
            isCompleted={isCompleted}
          />
        </div>
      )}

      <CardContent className="p-0 flex-1 overflow-y-auto scrollbar-thin">
        <Table>
          <TableHeader className="bg-muted/30 sticky top-0 z-10">
            <TableRow className="border-border/60 hover:bg-transparent">
              {["Investigation", "Value", "Unit", "Actions"].map((h, i) => (
                <TableHead
                  key={h}
                  className={`z-label text-muted-foreground h-9 ${i === 3 ? "text-right" : ""}`}
                >
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Draft rows */}
            {!isCompleted &&
              draftNotes.map((draft) => (
                <TableRow
                  key={`draft-${draft.id}`}
                  className="border-border/60 bg-primary/5 hover:bg-primary/10"
                >
                  <TableCell className="py-2">
                    <span className="z-body-md text-foreground">
                      {draft.name}
                    </span>
                    <span className="ml-2 inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 z-hint text-primary">
                      New
                    </span>
                  </TableCell>
                  <TableCell className="py-2">
                    <Input
                      type="number"
                      value={draft.value}
                      onChange={(e) =>
                        handleUpdateDraftValue(draft.id, e.target.value)
                      }
                      placeholder="Enter value"
                      className="h-8 z-body rounded-lg bg-muted/50 border-border/60"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveDraft(draft);
                      }}
                    />
                  </TableCell>
                  <TableCell className="py-2 z-caption text-muted-foreground">
                    {getInvestigationUnitDisplay(draft)}
                  </TableCell>
                  <TableCell className="text-right py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteDraft(draft.id)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                      <Button
                        size="icon"
                        className="h-8 w-8 rounded-xl"
                        onClick={() => handleSaveDraft(draft)}
                        disabled={!draft.value || createNote.isPending}
                      >
                        {createNote.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Save className="size-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}

            {/* Existing Notes */}
            {isLoadingNotes ? (
              <TableRow className="border-border/60 hover:bg-transparent">
                <TableCell colSpan={4} className="h-20 text-center">
                  <Loader2 className="size-5 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : flattenedNotes.length === 0 && draftNotes.length === 0 ? (
              <TableRow className="border-border/60 hover:bg-transparent">
                <TableCell colSpan={4} className="h-32 p-0">
                  <EmptyState
                    icon={<FileText />}
                    title="No investigation notes"
                    subtitle="Search and add investigations above"
                    variant="rich"
                    className="h-full border-0 bg-transparent"
                  />
                </TableCell>
              </TableRow>
            ) : (
              flattenedNotes.map((note) => (
                <TableRow
                  key={note.id}
                  className="group border-border/60 hover:bg-muted/20"
                >
                  <TableCell className="py-2 z-body-md text-foreground">
                    {note.investigation_item_details.name}
                  </TableCell>
                  <TableCell className="py-2 z-body-md text-foreground">
                    {note.value}
                  </TableCell>
                  <TableCell className="py-2 z-caption text-muted-foreground">
                    {getInvestigationUnitDisplay(
                      note.investigation_item_details
                    )}
                  </TableCell>
                  <TableCell className="text-right py-2">
                    {!isCompleted && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteNote(note.id)}
                        disabled={deleteNote.isPending}
                      >
                        {deleteNote.isPending ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <Trash2 className="size-4" />
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </div>
  );

  const ExpandedContent = () => {
    if (isLoadingNotes) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (!notes || notes.length === 0) {
      return (
        <EmptyState title="No investigation history found" variant="compact" />
      );
    }
    return (
      <div className="flex flex-col gap-5 overflow-y-auto h-full scrollbar-thin">
        {notes.map((group, index) => (
          <div
            key={`${group.zarity_appointment_id}-${index}`}
            className="flex flex-col gap-3"
          >
            {/* Group header */}
            <div className="flex items-center justify-between bg-muted/30 p-4 rounded-xl border border-border/60">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-primary bg-primary/10 px-3 py-1.5 rounded-lg">
                  <Calendar className="size-4" />
                  <span className="z-body-md z-numeric">
                    {format(new Date(group.created_at), "MMM dd, yyyy")}
                  </span>
                </div>
                <div className="h-4 w-px bg-border/60" />
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="size-4" />
                  <span className="z-body-md">{group.doctor_name}</span>
                </div>
              </div>
              <span className="z-hint z-numeric text-muted-foreground bg-muted px-2 py-1 rounded-lg border border-border/60">
                {group.zarity_appointment_id}
              </span>
            </div>

            {/* Group table */}
            <div className="border border-border/60 rounded-xl overflow-hidden bg-card">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow className="border-border/60">
                    {["Investigation", "Value", "Unit"].map((h) => (
                      <TableHead
                        key={h}
                        className="z-label text-muted-foreground"
                      >
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.investigation_items.map((item) => (
                    <TableRow
                      key={item.id}
                      className="border-border/60 hover:bg-muted/20"
                    >
                      <TableCell className="z-body-md text-foreground">
                        {item.investigation_item_details.name}
                      </TableCell>
                      <TableCell className="z-body-md text-foreground">
                        {item.value}
                      </TableCell>
                      <TableCell className="z-caption text-muted-foreground">
                        {getInvestigationUnitDisplay(
                          item.investigation_item_details
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Card className="rounded-3xl border-border/60 bg-card shadow-sm h-full flex flex-col overflow-hidden">
        <CardHeader className="p-4 flex flex-row items-center justify-between border-b border-border/60 shrink-0">
          <div className="flex items-center gap-2">
            <CardTitle className="z-h3 text-foreground">
              Investigation Notes
            </CardTitle>
            <Info className="size-4 text-muted-foreground" />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-xl text-muted-foreground hover:text-foreground"
            onClick={() => setIsExpanded(true)}
          >
            <History className="size-4" />
          </Button>
        </CardHeader>

        <CollapsedContent />
      </Card>

      <ExpandedCardDialog
        open={isExpanded}
        onOpenChange={setIsExpanded}
        title="Investigation History"
      >
        <div className="h-full">
          <ExpandedContent />
        </div>
      </ExpandedCardDialog>
    </>
  );
};
